import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiResponse } from "@/shared/types/api.ts";
import { QUERY_KEYS } from "@/polygon/api/config.ts";
import {
  getAllPolygons,
  createPolygon,
  getPolygonById,
  updatePolygon,
  patchPolygon,
  deletePolygon,
  ICreatePolygonInput,
  IUpdatePolygonInput,
  IPolygonResponse,
} from "@/polygon/api/requests/polygon.requests.ts";

// Query to get all polygons
export const useGetAllPolygons = () => {
  return useQuery<ApiResponse<IPolygonResponse[]>>({
    queryKey: [QUERY_KEYS.POLYGON],
    queryFn: getAllPolygons,
    staleTime: 1000 * 60 * 5, // 5 minutes - data considered fresh
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
  });
};

// Query to get a single polygon by ID
export const useGetPolygonById = (id: number) => {
  return useQuery<ApiResponse<IPolygonResponse>>({
    queryKey: [QUERY_KEYS.POLYGON, id],
    queryFn: () => getPolygonById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes - data considered fresh
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
  });
};

export const useCreatePolygon = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<IPolygonResponse>, Error, ICreatePolygonInput>({
    mutationFn: createPolygon,
    onSuccess: () => {
      // Invalidate and refetch polygons list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POLYGON] });
    },
    onError: (error) => {
      console.error("Error creating polygon:", error);
    },
  });
};

// Mutation to update a polygon (PUT)
export const useUpdatePolygon = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<IPolygonResponse>, Error, { id: number; data: IUpdatePolygonInput }>({
    mutationFn: ({ id, data }) => updatePolygon(id, data),
    onSuccess: (data, variables) => {
      // Invalidate and refetch polygons list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POLYGON] });
      // Update the specific polygon cache
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POLYGON, variables.id] });
    },
    onError: (error) => {
      console.error("Error updating polygon:", error);
    },
  });
};

// Mutation to partially update a polygon (PATCH)
export const usePatchPolygon = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<IPolygonResponse>, Error, { id: number; data: IUpdatePolygonInput }>({
    mutationFn: ({ id, data }) => patchPolygon(id, data),
    onSuccess: (data, variables) => {
      // Invalidate and refetch polygons list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POLYGON] });
      // Update the specific polygon cache
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POLYGON, variables.id] });
    },
    onError: (error) => {
      console.error("Error patching polygon:", error);
    },
  });
};

// Mutation to delete a polygon
export const useDeletePolygon = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: deletePolygon,
    onSuccess: (data, polygonId) => {
      // Invalidate and refetch polygons list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POLYGON] });
      // Remove the specific polygon from cache
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.POLYGON, polygonId] });
    },
    onError: (error) => {
      console.error("Error deleting polygon:", error);
    },
  });
};